import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CityProvider } from "@/context/CityContext";

import Landing from "@/pages/Landing";
import VisitorEntry from "@/pages/VisitorEntry";
import CitySelection from "@/pages/CitySelection";
import Marketplace from "@/pages/Marketplace";
import ProductDetail from "@/pages/ProductDetail";
import SignIn from "@/pages/SignIn";
import ChangePassword from "@/pages/ChangePassword";
import CompleteProfile from "@/pages/CompleteProfile";

import SellerLayout from "@/pages/seller/SellerLayout";
import SellerDashboard from "@/pages/seller/Dashboard";
import MyProducts from "@/pages/seller/MyProducts";
import AddProduct from "@/pages/seller/AddProduct";
import ManufacturerStock from "@/pages/seller/ManufacturerStock";
import Subscription from "@/pages/seller/Subscription";
import Billing from "@/pages/seller/Billing";
import Account from "@/pages/seller/Account";
import SellerHistory from "@/pages/seller/History";
import SellerLeadsCRM from "@/pages/seller/SellerLeadsCRM";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Sellers from "@/pages/admin/Sellers";
import IncompleteSignups from "@/pages/admin/IncompleteSignups";
import LeadsCRM from "@/pages/admin/LeadsCRM";
import AdminProducts from "@/pages/admin/AdminProducts";
import Payments from "@/pages/admin/Payments";
import Subscriptions from "@/pages/admin/Subscriptions";
import PackagePlanner from "@/pages/admin/PackagePlanner";
import Media from "@/pages/admin/Media";
import ClicksHistory from "@/pages/admin/ClicksHistory";
import VisitorDirectory from "@/pages/admin/VisitorDirectory";
import Cities from "@/pages/admin/Cities";
import Localities from "@/pages/admin/Localities";
import CategoryOptions from "@/pages/admin/CategoryOptions";
import SalesLog from "@/pages/admin/SalesLog";
import Settings from "@/pages/admin/Settings";

function Guard({ roles, children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/signin" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CityProvider>
            <Toaster position="top-center" richColors />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/welcome" element={<VisitorEntry />} />
              <Route path="/select-city" element={<CitySelection />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/change-password" element={<Guard><ChangePassword /></Guard>} />
              <Route path="/complete-profile" element={<Guard roles={["manufacturer", "wholesaler", "retailer"]}><CompleteProfile /></Guard>} />

              <Route path="/seller" element={<Guard roles={["manufacturer", "wholesaler", "retailer"]}><SellerLayout /></Guard>}>
                <Route index element={<SellerDashboard />} />
                <Route path="products" element={<MyProducts />} />
                <Route path="add-product" element={<AddProduct />} />
                <Route path="manufacturer-stock" element={<ManufacturerStock />} />
                <Route path="subscription" element={<Subscription />} />
                <Route path="billing" element={<Billing />} />
                <Route path="account" element={<Account />} />
                <Route path="history" element={<SellerHistory />} />
                <Route path="leads-crm" element={<SellerLeadsCRM />} />
              </Route>

              <Route path="/admin" element={<Guard roles={["admin"]}><AdminLayout /></Guard>}>
                <Route index element={<AdminDashboard />} />
                <Route path="sellers" element={<Sellers />} />
                <Route path="incomplete-signups" element={<IncompleteSignups />} />
                <Route path="leads" element={<LeadsCRM />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="categories" element={<CategoryOptions />} />
                <Route path="sales-log" element={<SalesLog />} />
                <Route path="payments" element={<Payments />} />
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="packages" element={<PackagePlanner />} />
                <Route path="media" element={<Media />} />
                <Route path="clicks" element={<ClicksHistory />} />
                <Route path="visitors" element={<VisitorDirectory />} />
                <Route path="cities" element={<Cities />} />
                <Route path="localities" element={<Localities />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CityProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
